import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCartStore } from '@/entities/cart';

export interface BottomNavigationProps {
  activeTab?: 'catalog' | 'favorites' | 'cart' | 'profile';
  onTabChange?: (tab: 'catalog' | 'favorites' | 'cart' | 'profile') => void;
}

export function BottomNavigation({
  onTabChange,
}: BottomNavigationProps): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const items = useCartStore((state) => state.items);
  const cartCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const getActiveTab = (): 'catalog' | 'favorites' | 'cart' | 'profile' => {
    if (location.pathname.startsWith('/catalog')) return 'catalog';
    if (location.pathname.startsWith('/favorites')) return 'favorites';
    if (location.pathname.startsWith('/cart')) return 'cart';
    if (location.pathname.startsWith('/profile')) return 'profile';
    return 'catalog';
  };

  const activeTab = getActiveTab();

  const tabs = [
    { id: 'catalog' as const, icon: '🏠', label: 'Каталог' },
    { id: 'favorites' as const, icon: '❤️', label: 'Избранное' },
    { id: 'cart' as const, icon: '🛒', label: 'Корзина', badge: cartCount > 0 ? cartCount : undefined },
    { id: 'profile' as const, icon: '👤', label: 'Профиль' },
  ];

  const handleClick = (tabId: typeof activeTab) => {
    onTabChange?.(tabId);
    navigate(tabId === 'catalog' ? '/catalog' : `/${tabId}`);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-tg-bg border-t border-gray-200 dark:border-slate-700 safe-area-bottom z-50">
      <div className="flex items-center justify-around h-16 pb-safe">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              className={`flex flex-col items-center justify-center w-full h-full py-2 transition-colors relative ${
                isActive
                  ? 'text-tg-button'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              onClick={() => handleClick(tab.id)}
              type="button"
              aria-label={tab.label}
            >
              <span className="text-xl mb-0.5">{tab.icon}</span>
              <span className="text-xs font-medium">{tab.label}</span>
              {tab.badge !== undefined && (
                <span className="absolute top-1 right-1/4 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full border-2 border-tg-bg">
                  {tab.badge}
                </span>
              )}
              {isActive && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-tg-button rounded-t-full" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
